import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiBaseOrigin, configuredApiOrigin } from '../../../../shared/config/api-origin';
import { createLiveLocalAssetAdapter } from '../adapters/live-local-asset-adapter';
import type { LocalAssetReadPort } from '../adapters/local-asset-port';
import {
  filterAssets,
  type LocalAssetCatalogResult,
  type LocalAssetDetailResult,
  type LocalAssetSnapshot,
} from '../consumer/asset-contract';
import {
  createAssetCatalogSession,
  createAssetDetailSession,
  initialAssetReadState,
  type AssetReadState,
} from '../consumer/read-assets';
import { assetDemoVisual } from '../fixtures/asset-demo-visuals';
export const PROVIDER_REFERENCE_NOTICE = '关联 Provider 的定义引用（非该装备已具备能力的证明）';
function phaseLabel<T>(state: AssetReadState<T>, kind: '目录' | '详情'): string {
  switch (state.phase) {
    case 'LOADING':
      return `正在读取装备${kind} · Loading`;
    case 'TRANSPORT_ERROR':
      return `装备${kind}传输错误`;
    case 'WIRE_INVALID':
      return `装备${kind} Wire Invalid`;
    case 'REQUEST_REJECTED':
      return '资产 ID 请求已拒绝';
    default:
      return kind === '目录' ? '本地装备目录尚未加载' : '装备详情尚未读取';
  }
}
export function AssetIllustration({
  id,
  compact = false,
}: {
  readonly id: string;
  readonly compact?: boolean;
}) {
  const visual = assetDemoVisual(id);
  const [failed, setFailed] = useState(false);
  return (
    <figure className={compact ? 'ca-center__thumbnail' : 'ca-center__illustration'}>
      {visual && !failed ? (
        <img
          src={visual.illustration}
          alt="合成示意图，非实物证据"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="ca-center__media-empty">暂无示意图片</div>
      )}
      {!compact && <figcaption>合成示意图 · 非实物证据</figcaption>}
    </figure>
  );
}
export function AssetCatalogWorkspace({
  state,
  load,
  cancel,
  showDetail,
  selectedId = null,
}: {
  readonly state: AssetReadState<LocalAssetCatalogResult>;
  readonly load: () => void;
  readonly cancel: () => void;
  readonly showDetail: (id: string, trigger: HTMLButtonElement) => void;
  readonly selectedId?: string | null;
}) {
  const [text, setText] = useState('');
  const items = state.result?.status === 'SUCCESS' ? (state.result.items ?? []) : [];
  const filtered = filterAssets(items, text);
  const title =
    state.phase === 'RESULT'
      ? state.result?.status === 'SOURCE_UNAVAILABLE'
        ? '装备来源不可读 · SOURCE_UNAVAILABLE'
        : items.length === 0
          ? '装备目录读取成功 · 空目录'
          : '装备目录读取成功'
      : phaseLabel(state, '目录');
  return (
    <section className="ca-center__card ca-center__assets" aria-labelledby="ca-asset-title">
      <div className="ca-center__section-heading">
        <h2 id="ca-asset-title">
          装备列表 {state.result?.status === 'SUCCESS' && `(${items.length})`}
        </h2>
      </div>
      <button className="ca-center__primary" type="button" onClick={load}>
        {state.phase === 'NOT_LOADED' ? '加载本地装备目录' : '重新加载本地装备目录'}
      </button>
      <p className="ca-center__scope">LOCAL_SYNTHETIC 本次目录 · 只读 · 非行动授权</p>
      <div className="ca-center__catalog-feedback" aria-live="polite" aria-atomic="true">
        <h3 data-asset-status>{title}</h3>
        {state.phase === 'NOT_LOADED' && <p>主动加载后查看本地合成 Owner 记录。</p>}
        {state.phase === 'LOADING' && (
          <>
            <p>等待本次真实 HTTP 返回；不保留旧成功列表。</p>
            <button type="button" onClick={cancel}>
              取消装备目录读取
            </button>
          </>
        )}
        {(state.phase === 'WIRE_INVALID' || state.phase === 'TRANSPORT_ERROR') && (
          <p>未取得可信目录。可重试，失败不会变成示例成功数据。</p>
        )}
        {state.result?.status === 'SOURCE_UNAVAILABLE' && (
          <p>来源不可取与成功空目录不同；可重新加载。</p>
        )}
      </div>
      {state.result?.status === 'SUCCESS' && (
        <>
          <div className="ca-center__catalog-meta">
            <span data-asset-count>
              本次返回 {items.length} 条装备记录 · 当前筛选 {filtered.length} 条
            </span>
            <span>
              {state.result.coverage === 'TRUNCATED_AT_LIMIT'
                ? '装备目录达到上限，已截断'
                : '本次合成来源未截断'}
            </span>
          </div>
          <label className="ca-center__asset-filter">
            装备名称 / 资产 ID 筛选
            <input
              placeholder="搜索装备名称或资产 ID…"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </label>
          <p className="ca-center__scope">仅筛选本次后端返回结果，不发起服务器搜索。</p>
          {items.length === 0 ? (
            <p className="ca-center__empty">当前合成来源没有资产记录；这是成功空目录。</p>
          ) : filtered.length === 0 ? (
            <p className="ca-center__empty">装备当前筛选无匹配。</p>
          ) : (
            <ul className="ca-center__asset-entries">
              {filtered.map((asset) => (
                <li key={asset.assetId} data-asset-id={asset.assetId}>
                  <button
                    type="button"
                    aria-label={`查看装备详情：${asset.assetId}`}
                    aria-pressed={selectedId === asset.assetId}
                    onClick={(event) => showDetail(asset.assetId, event.currentTarget)}
                  >
                    <AssetIllustration key={asset.assetId} id={asset.assetId} compact />
                    <span className="ca-center__asset-row-copy">
                      <strong>{asset.displayName}</strong>
                      <code>{asset.assetId}</code>
                      <small>合成示意 · 非实物证据</small>
                      <small>定义引用：非该装备已具备能力的证明</small>
                    </span>
                    <span aria-hidden="true">›</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <details className="ca-center__catalog-wire">
            <summary>装备来源范围与读取限制</summary>
            <p>
              {state.result.scope} · limit {state.result.limit} · {state.result.coverage} ·
              NON_AUTHORITY。未截断只描述本次合成来源，不表示真实机构资产完整。
            </p>
            <p>本资产能力关系：UNKNOWN / NOT ESTABLISHED</p>
          </details>
        </>
      )}
    </section>
  );
}
export function AssetIdentityFields({
  snapshot,
  onDefinition,
}: {
  readonly snapshot: LocalAssetSnapshot;
  readonly onDefinition?: ((id: string, trigger: HTMLButtonElement) => void) | undefined;
}) {
  const asset = snapshot;
  return (
    <>
      <section className="ca-center__card">
        <h3>装备档案</h3>
        <dl className="ca-center__fields">
          <div>
            <dt>资产 ID</dt>
            <dd data-asset-field="assetId">{asset.assetId}</dd>
          </div>
          <div>
            <dt>显示名称</dt>
            <dd data-asset-field="displayName">{asset.displayName}</dd>
          </div>
          <div>
            <dt>资产版本</dt>
            <dd data-asset-field="assetVersion">{asset.assetVersion}</dd>
          </div>
          <div>
            <dt>来源</dt>
            <dd data-asset-field="originLabel">{asset.originLabel}</dd>
          </div>
        </dl>
      </section>
      <section className="ca-center__card">
        <h3>关联提供者（Provider）</h3>
        <dl className="ca-center__fields">
          <div>
            <dt>Provider ID</dt>
            <dd data-asset-field="providerId">{asset.provider.providerId}</dd>
          </div>
          <div>
            <dt>Provider 名称</dt>
            <dd>{asset.provider.displayName}</dd>
          </div>
          <div>
            <dt>Provider 版本</dt>
            <dd>{asset.provider.providerVersion}</dd>
          </div>
          <div>
            <dt>Provider 来源</dt>
            <dd>{asset.provider.originLabel}</dd>
          </div>
        </dl>
      </section>
      <section className="ca-center__card ca-center__asset-relations">
        <h3>Provider 级定义引用</h3>
        <p>{PROVIDER_REFERENCE_NOTICE}</p>
        {asset.provider.definitionReferences.length === 0 ? (
          <p>Provider 当前没有定义引用；本资产能力关系仍为未知。</p>
        ) : (
          <ul>
            {asset.provider.definitionReferences.map((reference) => (
              <li key={reference.capabilityId}>
                <code>{reference.capabilityId}</code>
                <span>定义版本 {reference.definitionVersion}</span>
                <details>
                  <summary>查看准确摘要</summary>
                  <code>{reference.definitionDigest}</code>
                </details>
                {onDefinition && (
                  <button
                    type="button"
                    onClick={(event) => onDefinition(reference.capabilityId, event.currentTarget)}
                  >
                    按 ID 只读查询定义
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="ca-center__card">
        <h3>机构／型号／认证／运行资料尚未建立</h3>
        <dl className="ca-center__fields">
          <div>
            <dt>所属组织</dt>
            <dd>
              {asset.organization.status} / {asset.organization.evidence}
            </dd>
          </div>
          <div>
            <dt>产品型号</dt>
            <dd>
              {asset.productModel.status} / {asset.productModel.evidence}
            </dd>
          </div>
          {['认证状态', '运行状态', '维护信息', 'Mission 使用历史'].map((label) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>UNKNOWN（未获权威来源提供／未接入）</dd>
            </div>
          ))}
        </dl>
        <p>未接入不表示从未使用或无需维护，不能据此判断准备度或行动资格。</p>
      </section>
    </>
  );
}
export function AssetDetailContent({
  state,
  retry,
  cancel,
  onDefinition,
}: {
  readonly state: AssetReadState<LocalAssetDetailResult>;
  readonly retry: () => void;
  readonly cancel: () => void;
  readonly onDefinition?: ((id: string, trigger: HTMLButtonElement) => void) | undefined;
}) {
  const result = state.result;
  const snapshot = result?.status === 'AVAILABLE' ? result.snapshot : null;
  const visual = snapshot ? assetDemoVisual(snapshot.assetId) : undefined;
  const title =
    state.phase === 'RESULT'
      ? result?.status === 'AVAILABLE'
        ? '装备记录已读取 · AVAILABLE'
        : result?.status === 'UNKNOWN'
          ? '未找到资产记录 · UNKNOWN'
          : '装备读取来源不可取 · UNAVAILABLE'
      : phaseLabel(state, '详情');
  return (
    <div className="ca-center__asset-detail-grid">
      <section className="ca-center__card ca-center__asset-main">
        <div className="ca-center__catalog-feedback" aria-live="polite">
          <h3 data-asset-detail-status>{title}</h3>
          <p>
            资产 ID：<code>{state.assetId}</code>
          </p>
          {state.phase === 'LOADING' ? (
            <button type="button" onClick={cancel}>
              取消装备详情读取
            </button>
          ) : (
            <button type="button" onClick={retry}>
              重新读取装备详情
            </button>
          )}
        </div>
        {snapshot ? (
          <>
            <h2>{snapshot.displayName}</h2>
            <p className="ca-center__scope">本地合成数据 · 非运行装备</p>
            <section className="ca-center__asset-unknown">
              <h3>本装备自身能力</h3>
              <p data-asset-capability>
                {snapshot.assetCapabilityStatus} / {snapshot.assetCapabilityEvidence}（未建立证据）
              </p>
              <p>Provider 级引用不证明本装备具备能力。</p>
            </section>
            <div className="ca-center__visual-story">
              <AssetIllustration key={snapshot.assetId} id={snapshot.assetId} />
              <section className="ca-center__demo-description" data-demo-presentation>
                <p className="ca-center__demo-label">演示介绍 · 未经验证 · 仅供排版演示</p>
                <h3>用途与作用示意</h3>
                {visual ? (
                  <p>{visual.introduction}</p>
                ) : (
                  <p>暂无演示介绍；不从名称或 Provider 引用推断用途。</p>
                )}
                <h3>场景创意（未经适用性评估）</h3>
                {visual ? (
                  <ul>
                    {visual.scenes.map((scene) => (
                      <li key={scene}>{scene}</li>
                    ))}
                  </ul>
                ) : (
                  <p>暂无演示场景。</p>
                )}
              </section>
            </div>
            <details className="ca-center__catalog-wire">
              <summary>数据可信范围与补充说明</summary>
              <p>AVAILABLE 仅表示取得匹配资产记录；不表示设备可用、在线或可调度。</p>
              <p>
                REAL_LOCAL_SYNTHETIC_HTTP · NON_AUTHORITY ·
                来源为非运行合成数据。示意图片、介绍及场景不参与匹配、筛选评分、导出或决策。
              </p>
            </details>
          </>
        ) : (
          <p className="ca-center__empty">
            本次没有可信资产详情。Unknown、来源不可取、Wire 和传输错误分别保留。
          </p>
        )}
      </section>
      <aside className="ca-center__asset-records" aria-label="资产与 Provider 真实读取档案">
        {snapshot ? (
          <AssetIdentityFields snapshot={snapshot} onDefinition={onDefinition} />
        ) : (
          <section className="ca-center__card">
            <h3>资产／Provider 档案</h3>
            <p>等待本次可信详情；不展示旧快照或演示档案。</p>
          </section>
        )}
      </aside>
    </div>
  );
}
export function LocalAssetPanel({
  port,
  onDefinition,
}: {
  readonly port?: LocalAssetReadPort;
  readonly onDefinition?: ((id: string, trigger: HTMLButtonElement) => void) | undefined;
}) {
  const enabled =
    typeof window !== 'undefined' && (import.meta.env.DEV || configuredApiOrigin() !== null);
  const live = useMemo(
    () => (enabled ? (port ?? createLiveLocalAssetAdapter({ baseOrigin: apiBaseOrigin() })) : null),
    [enabled, port],
  );
  const [catalog, setCatalog] = useState(initialAssetReadState<LocalAssetCatalogResult>);
  const [detail, setDetail] = useState(initialAssetReadState<LocalAssetDetailResult>);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const catalogSession = useRef<ReturnType<typeof createAssetCatalogSession> | null>(null),
    detailSession = useRef<ReturnType<typeof createAssetDetailSession> | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null),
    scroll = useRef(0),
    closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!live) return;
    const c = createAssetCatalogSession(live, setCatalog),
      d = createAssetDetailSession(live, setDetail);
    catalogSession.current = c;
    detailSession.current = d;
    return () => {
      c.dispose();
      d.dispose();
      catalogSession.current = null;
      detailSession.current = null;
    };
  }, [live]);
  const close = useCallback(() => {
    detailSession.current?.cancel();
    setSelectedId(null);
    requestAnimationFrame(() => {
      if (trigger.current?.isConnected) trigger.current.focus({ preventScroll: true });
      window.scrollTo(0, scroll.current);
    });
  }, []);
  useEffect(() => {
    if (selectedId === null) return;
    function escape(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target;
      const withinPanel =
        target instanceof Element && target.closest('.ca-center__asset-workbench') !== null;
      if (target !== document.body && !withinPanel) return;
      if (target instanceof Element && target.closest('dialog[open], [aria-modal="true"]')) return;
      event.preventDefault();
      close();
    }
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [selectedId, close]);
  if (!enabled)
    return (
      <section className="ca-center__card">
        <h2>生产态装备读取入口未开放</h2>
        <p>本地合成装备只在开发态显式读取。</p>
      </section>
    );
  return (
    <div className="ca-center__asset-workbench" data-detail-open={selectedId !== null}>
      <AssetCatalogWorkspace
        state={catalog}
        load={() => {
          close();
          void catalogSession.current?.load();
        }}
        cancel={() => catalogSession.current?.cancel()}
        selectedId={selectedId}
        showDetail={(id, button) => {
          scroll.current = window.scrollY;
          trigger.current = button;
          setSelectedId(id);
          void detailSession.current?.load(id);
          if (window.matchMedia('(max-width: 700px)').matches)
            requestAnimationFrame(() => {
              closeButton.current?.focus({ preventScroll: true });
              const button = closeButton.current;
              if (button) {
                const noticeHeight =
                  document.querySelector('.ca-center__notice')?.getBoundingClientRect().height ?? 0;
                window.scrollTo(
                  0,
                  window.scrollY + button.getBoundingClientRect().top - noticeHeight - 12,
                );
              }
            });
        }}
      />
      <section className="ca-center__detail-workspace" aria-label="装备详情" tabIndex={-1}>
        {selectedId ? (
          <>
            <button className="ca-center__return" type="button" ref={closeButton} onClick={close}>
              关闭装备详情并返回
            </button>
            <AssetDetailContent
              state={detail}
              retry={() => {
                void detailSession.current?.load(selectedId);
              }}
              cancel={() => detailSession.current?.cancel()}
              onDefinition={onDefinition}
            />
          </>
        ) : (
          <div className="ca-center__asset-detail-grid">
            <section className="ca-center__card ca-center__select-hint">
              <div aria-hidden="true">◇</div>
              <h2>选择一台装备</h2>
              <p>从左侧本次目录读取详情，核对资产与 Provider。</p>
              <p>本装备自身能力：UNKNOWN / NOT ESTABLISHED</p>
            </section>
            <aside className="ca-center__card">
              <h3>资产／Provider 档案</h3>
              <p>选择装备后显示真实 Owner 详情。</p>
              <p className="ca-center__scope">组织、认证、运行、维护与 Mission 历史尚未接入。</p>
            </aside>
          </div>
        )}
      </section>
    </div>
  );
}
